// src/modules/soe/RelatorioDashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';
import { formatarData, getNormalizedDateString } from '../../utils/helpers';
import { Modal } from '../../components/Modal'; 
import { RelatorioEstagioModal } from '../../components/RelatorioEstagioModal'; // Usada para modo de leitura

// --- Ícones ---
const ReportsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
);
const ResetIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.926 8.926 0 0112 21a9 9 0 01-5.6-3.2m15.356-2A8.926 8.926 0 0012 3a9 9 0 00-5.6 3.2" /></svg>
);
const FileTextIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);
const EyeIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
);
const PrinterIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
);


// --- CONSTANTES DE PERGUNTAS PARA LEITURA ---
const cmtRatingQuestions = [
    { label: 'Disciplina', name: 'disciplina' },
    { label: 'Pontualidade', name: 'pontualidade' },
    { label: 'Apresentação Pessoal', name: 'apresentacao_pessoal' },
    { label: 'Interação com a Tropa', name: 'interacao_tropa' },
    { label: 'Interação com o Público', name: 'interacao_publico' },
    { label: 'Responsabilidade', name: 'responsabilidade' },
    { label: 'Dedicação e Interesse', name: 'dedicacao_interesse' },
    { label: 'Iniciativa', name: 'iniciativa' },
    { label: 'Capacidade de Decisão', name: 'capacidade_decisao' },
    { label: 'Comprometimento Institucional', name: 'comprometimento_instituicao' },
    { label: 'Traços de Liderança', name: 'tracos_lideranca' },
    { label: 'Conhecimento Técnico', name: 'conhecimento_tecnico' },
    { label: 'Estabilidade Emocional', name: 'estabilidade_emocional' },
    { label: 'Conhecimento Jurídico', name: 'conhecimento_juridico' },
    { label: 'Autoconfiança', name: 'autoconfianca' },
];

// --- COMPONENTE DE LEITURA (READ-ONLY) ---
const RenderRelatorioLeitura = ({ data, escolha }) => {
    if (!data) return (
         <div className="p-8 text-center text-gray-500">Relatório não encontrado ou não iniciado pelo cadete.</div>
    );
    const p = data.preceptor || {};
    const c = data.cadete || {};

    return (
        <div className="space-y-6 p-2 text-sm text-gray-800 max-h-[70vh] overflow-y-auto">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-gray-500 uppercase font-bold">Cadete</p><p className="font-medium">{data?.cadete_nome}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase font-bold">Local / Data</p><p className="font-medium">{data?.local_nome} - {formatarData(data?.data_estagio)}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase font-bold">Período</p><p className="font-medium uppercase">{c.periodo || escolha?.turno || 'N/D'}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase font-bold">QRA Preceptor Cadete</p><p className="font-medium">{c.qra_preceptor || 'N/D'}</p></div>
                </div>
            </div>
            <div className="border-t pt-4">
                <h4 className="text-lg font-bold text-blue-800 mb-3">Relatório do Aluno Oficial</h4>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div><p className="font-bold text-gray-700 text-xs">Modelo VTR:</p><p className="bg-white p-2 rounded border">{c.modelo_vtr || '-'}</p></div>
                        <div><p className="font-bold text-gray-700 text-xs">Prefixo:</p><p className="bg-white p-2 rounded border">{c.prefixo || '-'}</p></div>
                    </div>
                    <div><p className="font-bold text-gray-700 text-xs mb-1">Área de Conhecimento / Justificativa:</p><p className="bg-white p-2 rounded border"><span className="font-bold">{c.area_conhecimento || 'N/A'}</span> - {c.justificativa_area || '-'}</p></div>
                    <div><p className="font-bold text-gray-700 text-xs mb-1">Acontecimento Importante:</p><p className="italic bg-white p-2 rounded border">{c.acontecimento_importante || 'Nenhum relato.'}</p></div>
                    <div><p className="font-bold text-gray-700 text-xs mb-1">Correspondência com Ensino APMBB:</p><p className="italic bg-white p-2 rounded border">{c.indicacao_preceptor || 'Nenhum relato.'}</p></div>
                    <div><p className="font-bold text-gray-700 text-xs mb-1">Dificuldades e Soluções:</p><p className="italic bg-white p-2 rounded border">{c.dificuldades_solucoes || 'Nenhum relato.'}</p></div>
                    <div><p className="font-bold text-gray-700 text-xs mb-1">Indicaria Estágio?</p><p className="bg-white p-2 rounded border">{c.indicaria_estagio || '-'}</p></div>
                </div>
            </div>
            <div className="border-t pt-4">
                <h4 className="text-lg font-bold text-blue-800 mb-3">Avaliação do Oficial Preceptor</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div><p className="font-bold text-gray-700">Oficial Avaliador:</p><p>{p.posto_nome || 'Aguardando...'}</p></div>
                    <div><p className="font-bold text-gray-700">Contato:</p><p>{p.telefone || 'N/A'}</p></div>
                    <div><p className="font-bold text-gray-700">Data Avaliação:</p><p>{p.data_avaliacao || 'N/A'}</p></div>
                    <div><p className="font-bold text-gray-700">Função:</p><p>{p.funcao_executada || 'N/A'}</p></div>
                </div>
                {p.nota_geral ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 bg-white p-3 rounded border">
                            {cmtRatingQuestions.map((q) => (
                                <div key={q.name} className="flex justify-between items-center border-b border-gray-100 pb-1 last:border-0">
                                    <span className="text-gray-600 text-xs">{q.label}</span>
                                    <span className="font-bold text-blue-600">{p[q.name] ?? '-'}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between border-t border-blue-200 pt-4 mt-4">
                            <span className="text-xl font-bold text-gray-800">NOTA FINAL:</span>
                            <span className="text-4xl font-extrabold text-blue-700">{p.nota_geral}</span>
                        </div>
                        <div className="space-y-2 pt-4">
                           <p className="font-bold text-gray-700 text-xs mb-1">Características Positivas:</p>
                           <p className="bg-white p-2 rounded border">{p.caracteristicas_positivas || 'N/A'}</p>
                           <p className="font-bold text-gray-700 text-xs mb-1">Deficiências/Dificuldades:</p>
                           <p className="bg-white p-2 rounded border">{p.deficiencias_dificuldades || 'N/A'}</p>
                        </div>
                    </div>
                ) : (
                    <p className="italic text-orange-600 bg-orange-50 p-2 rounded border border-orange-200">Avaliação ainda não preenchida pelo Preceptor.</p>
                )}
            </div>
        </div>
    );
};


export const RelatorioDashboard = () => {
    const { userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false); 
    const [error, setError] = useState('');
    
    // Dados
    const [todasEscolhas, setTodasEscolhas] = useState([]);
    const [allPelotoes, setAllPelotoes] = useState([]);
    const [allRelatorios, setAllRelatorios] = useState([]);
    
    // Extrato e Visualização
    const [showExtratoModal, setShowExtratoModal] = useState(false);
    const [extratoFiltros, setExtratoFiltros] = useState({ cadete: '', data: '', status: 'All', pelotao: 'All' }); 
    const [extratoListaPlana, setExtratoListaPlana] = useState([]);

    const [showReportViewModal, setShowReportViewModal] = useState(false);
    const [reportViewData, setReportViewData] = useState(null); 
    const [reportViewEscolha, setReportViewEscolha] = useState(null); 
    
    const pelotaoMap = useMemo(() => new Map(allPelotoes.map(p => [p.id, p])), [allPelotoes]);
    const hojeStr = useMemo(() => getNormalizedDateString(new Date()), []);

    // --- Mapeamento de Cores para Status ---
    const colorMap = {
        blue: { bgLight: 'bg-blue-50', text: 'text-blue-800', bgDark: 'bg-blue-500' },
        green: { bgLight: 'bg-green-50', text: 'text-green-800', bgDark: 'bg-green-500' },
        yellow: { bgLight: 'bg-yellow-50', text: 'text-yellow-800', bgDark: 'bg-yellow-500' },
        red: { bgLight: 'bg-red-50', text: 'text-red-800', bgDark: 'bg-red-500' },
        gray: { bgLight: 'bg-gray-50', text: 'text-gray-800', bgDark: 'bg-gray-500' },
    };

    // 1. Carregamento de Dados
    useEffect(() => {
        setLoading(true);
        const unsubEscolhas = onSnapshot(collection(db, paths.escolhas), (snapshot) => { setTodasEscolhas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); });
        const unsubPelotoes = onSnapshot(collection(db, paths.pelotoes), (snapshot) => { setAllPelotoes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); });
        const unsubRelatorios = onSnapshot(collection(db, paths.relatoriosEstagio), (snapshot) => { setAllRelatorios(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); setLoading(false); });
        
        return () => { unsubEscolhas(); unsubPelotoes(); unsubRelatorios(); };
    }, []);

    // 2. Lógica de Processamento do Extrato (Lista Plana)
    const processarExtratoListaPlana = useCallback(() => {
        const listaPlana = [];
        const hojeStr = getNormalizedDateString(new Date());

        allRelatorios.forEach(r => {
            let relatorioStatus = 'Pendente'; 
            let providencia = 'Cadete - Preencher Dados Iniciais';
            
            if (r.preceptor?.posto_nome) { 
                relatorioStatus = 'Avaliado';
                providencia = 'SOE - Arquivar/Analisar';
            } else if (r.cadete?.regiao) { 
                relatorioStatus = 'Rascunho'; 
                providencia = 'Cadete - Finalizar Relatório (Observações)';
            }

            if (relatorioStatus !== 'Avaliado' && r.data_estagio && getNormalizedDateString(r.data_estagio) < hojeStr) {
                providencia = 'Preceptor - Avaliar (Estágio Realizado)';
                relatorioStatus = 'Aguardando Preceptor';
            }

            const pelotaoInfo = pelotaoMap.get(r.pelotao_id);
            const pelotaoNomeDisplay = pelotaoInfo?.nome || r.pelotao_nome || 'N/D'; 
            
            listaPlana.push({
                pelotao: pelotaoNomeDisplay,
                cadete: r.cadete_nome,
                local: r.local_nome,
                data: r.data_estagio,
                dataStr: r.data_estagio ? getNormalizedDateString(r.data_estagio) : 'N/D',
                dataDisplay: r.data_estagio ? formatarData(r.data_estagio) : 'N/D',
                status: relatorioStatus,
                providencia: providencia,
                preceptor: r.cadete?.qra_preceptor || r.preceptor?.posto_nome || 'N/D',
                relatorioId: r.id,
                escolha_id: r.escolha_id,
            });
        });
        
        return listaPlana.sort((a, b) => {
            if (a.pelotao !== b.pelotao) { return a.pelotao.localeCompare(b.pelotao); }
            const dataA = a.data?.toDate() || 0;
            const dataB = b.data?.toDate() || 0;
            return dataA - dataB;
        });
    }, [allRelatorios, pelotaoMap, hojeStr]);

    // 3. Efeito para atualizar a lista plana
    useEffect(() => { 
        setExtratoListaPlana(processarExtratoListaPlana());
    }, [allRelatorios, processarExtratoListaPlana]);

    // 4. Lógica de Filtro para o Modal de Extrato
    const filteredExtratoLista = useMemo(() => {
        const { cadete, data, status, pelotao } = extratoFiltros;
        let lista = extratoListaPlana;
        const searchTerms = (cadete || '').toLowerCase();
        
        if (searchTerms) {
            lista = lista.filter(item => 
                (item.cadete || '').toLowerCase().includes(searchTerms) ||
                (item.preceptor || '').toLowerCase().includes(searchTerms)
            );
        }
        
        if (data) {
            lista = lista.filter(item => item.dataStr === data);
        }
        
        if (status !== 'All') {
            lista = lista.filter(item => item.status === status);
        }

        if (pelotao !== 'All') {
            lista = lista.filter(item => item.pelotao === pelotao);
        }

        return lista;
    }, [extratoListaPlana, extratoFiltros]);


    // 5. Handlers de Ação
    const handleOpenReportView = useCallback((item) => { 
        const escolha = todasEscolhas.find(e => e.id === item.escolha_id);
        if (!escolha) {
            alert("Escolha do estágio não encontrada. Não é possível abrir o relatório.");
            return;
        }
        const relatorio = allRelatorios.find(r => r.id === item.relatorioId) || allRelatorios.find(r => r.escolha_id === escolha.id);
        
        setReportViewEscolha(escolha);
        setReportViewData(relatorio); 
        setShowReportViewModal(true); 
    }, [todasEscolhas, allRelatorios]);

    const handleResetPreceptorReport = async (relatorioId, cadeteNome) => {
        if (!window.confirm(`ATENÇÃO: Tem certeza que deseja RESETAR a avaliação do Oficial Preceptor para o cadete ${cadeteNome}? O Oficial terá que preencher novamente.`)) return;

        setProcessing(true);
        setError('');
        try {
            const docRef = doc(db, paths.relatoriosEstagio, relatorioId);
            await updateDoc(docRef, {
                preceptor: {},
                ultima_atualizacao: serverTimestamp(),
            });

            await logAuditoria(userProfile, "Reset Avaliação Preceptor", { relatorioId, cadete: cadeteNome });
            alert("Avaliação do Preceptor resetada com sucesso.");

        } catch (err) {
            console.error("Erro ao resetar avaliação:", err);
            setError(err.message);
        } finally {
            setProcessing(false);
        }
    };
    
    const handlePrintExtrato = () => {
        if (filteredExtratoLista.length === 0) {
            alert("Não há dados para imprimir. Aplique os filtros e tente novamente.");
            return;
        }
        const dataGeracao = new Date().toLocaleString('pt-BR');
        let tableHtml = `<table class="w-full border-collapse"><thead><tr class="bg-gray-200"><th class="border p-2 text-left">Pelotão</th><th class="border p-2 text-left">Cadete / Local</th><th class="border p-2 text-left">Preceptor (QRA)</th><th class="border p-2 text-left">Status</th><th class="border p-2 text-left">Providência Necessária</th></tr></thead><tbody>`;
        filteredExtratoLista.forEach(item => {
            tableHtml += `<tr class="hover:bg-gray-50"><td class="border p-2">${item.pelotao}</td><td class="border p-2"><span class="font-semibold">${item.cadete}</span><span class="block text-xs text-gray-500">${item.local} (${item.dataDisplay})</span></td><td class="border p-2">${item.preceptor}</td><td class="border p-2">${item.status}</td><td class="border p-2 text-red-600">${item.providencia}</td></tr>`;
        });
        tableHtml += `</tbody></table>`;
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Extrato de Providências</title>
                <script src="https://cdn.tailwindcss.com"></script>
                <style> 
                    @page { size: A4 landscape; margin: 1.5cm; } 
                    body { font-family: "Inter", sans-serif; font-size: 9pt; } 
                    table { width: 100%; border-collapse: collapse; } 
                    th, td { border: 1px solid #ccc; padding: 4px; text-align: left; } 
                    th { background-color: #f2f2f2; } 
                    .no-print { display: none; } 
                </style>
            </head>
            <body class="p-4">
                <div class="flex items-center justify-between mb-4">
                    <h1 class="text-xl font-bold text-gray-800">Extrato de Providências de Relatórios</h1>
                    <p class="text-sm text-gray-600">Gerado em: ${dataGeracao}</p>
                </div>
                ${tableHtml}
                <footer class="text-center text-xs text-gray-500 mt-4 pt-4 border-t">Relatório gerado pelo SIAAP - Sistema Integrado Acadêmico da APMBB</footer>
                <button onclick="window.print()" class="no-print bg-blue-600 text-white px-4 py-2 rounded-lg fixed bottom-4 right-4">Imprimir</button>
            </body>
            </html>
        `);
        printWindow.document.close();
    };


    if (loading) return <LoadingSpinner text="Carregando dados de relatórios..." />;

    const relatoriosComStatus = allRelatorios.map(r => {
        let status = 'Pendente';
        const hojeStr = getNormalizedDateString(new Date());
        
        if (r.preceptor?.posto_nome) {
            status = 'Avaliados (Finalizado)';
        } else if (r.cadete?.regiao) {
            status = 'Rascunho (Cadete)';
        }
        
        if (status !== 'Avaliados (Finalizado)' && r.data_estagio && getNormalizedDateString(r.data_estagio) < hojeStr) {
            status = 'Aguardando Preceptor (Atrasado)';
        }
        
        const escolha = todasEscolhas.find(e => e.id === r.escolha_id);
        
        return {
            ...r,
            displayStatus: status,
            isEvaluated: !!r.preceptor?.posto_nome,
            isDraft: !!r.cadete?.regiao && !r.preceptor?.posto_nome,
            escolha: escolha,
            escolha_id: r.escolha_id,
        };
    }).sort((a, b) => (b.data_estagio?.toDate() || 0) - (a.data_estagio?.toDate() || 0));

    
    return (
        <div className="space-y-6 animate-fade-in">
            <header className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Gerenciamento de Relatórios</h1>
                <p className="text-gray-600">Visão geral dos relatórios de estágio dos cadetes.</p>
            </header>
        
            {/* Exibição de Erros */}
            {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between"><strong>Erro:</strong> {error} <button onClick={() => setError('')}>x</button></div>}

            <div className="flex justify-between items-center pb-3 border-b">
                <h3 className="text-xl font-semibold text-gray-800">Relatórios de Estágio ({allRelatorios.length})</h3>
                <Button onClick={() => setShowExtratoModal(true)} variant="primary">
                    Emitir Extrato de Providências
                </Button>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-x-auto border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cadete / Pelotão</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estágio / Data</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avaliado por</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {relatoriosComStatus.map(r => {
                            const pelotao = pelotaoMap.get(r.pelotao_id);
                            
                            let statusClass = 'bg-gray-100 text-gray-800';
                            if (r.displayStatus.includes('Avaliados')) statusClass = 'bg-green-100 text-green-800';
                            else if (r.displayStatus.includes('Aguardando')) statusClass = 'bg-yellow-100 text-yellow-800';
                            else if (r.displayStatus.includes('Rascunho')) statusClass = 'bg-blue-100 text-blue-800';
                            else if (r.displayStatus.includes('Pendente')) statusClass = 'bg-red-100 text-red-800';

                            return (
                                <tr key={r.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                                        {r.cadete_nome}
                                        <span className="block text-xs text-gray-500">{pelotao?.nome || r.pelotao_nome || 'N/D'}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        {r.local_nome}
                                        <span className="block text-xs text-gray-500">{r.data_estagio ? formatarData(r.data_estagio) : 'N/D'}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass}`}>
                                            {r.displayStatus}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {r.isEvaluated ? r.preceptor.posto_nome : (r.isDraft ? 'Cadete (Rascunho)' : 'N/A')}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end space-x-2">
                                        <Button 
                                            onClick={() => handleOpenReportView(r)} 
                                            variant="outline" 
                                            className="text-xs py-1 px-2 flex items-center gap-1"
                                            disabled={!r.escolha} 
                                            title={!r.escolha ? "A escolha original foi deletada." : "Ver Relatório"}
                                        >
                                            <FileTextIcon /> Ver
                                        </Button>
                                        <Button 
                                            onClick={() => handleResetPreceptorReport(r.id, r.cadete_nome)} 
                                            variant="danger" 
                                            className="text-xs py-1 px-2 flex items-center gap-1"
                                            disabled={processing || !r.isEvaluated} 
                                            title={r.isEvaluated ? "Zera a avaliação do Oficial Preceptor, mas mantém a do cadete." : "Só pode resetar se já houver uma avaliação do Preceptor."}
                                        >
                                            <ResetIcon /> Resetar Aval.
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                        {relatoriosComStatus.length === 0 && (<tr><td colSpan="5" className="text-center py-8 text-gray-500">Nenhum relatório encontrado.</td></tr>)}
                    </tbody>
                </table>
            </div>


            {/* Modais de Suporte */}
            <Modal 
                show={showReportViewModal} 
                onClose={() => setShowReportViewModal(false)} 
                title={`Relatório de Estágio - ${reportViewEscolha?.local_nome || 'N/D'}`} 
                size="4xl" 
                zIndex="z-[60]" // Garante que sobreponha o modal de Extrato (z-50)
            >
                <RenderRelatorioLeitura data={reportViewData} escolha={reportViewEscolha} />
                <div className="flex justify-end pt-4 border-t">
                    <Button onClick={() => setShowReportViewModal(false)} variant="secondary">Fechar</Button>
                </div>
            </Modal>
            
            <Modal show={showExtratoModal} onClose={() => setShowExtratoModal(false)} title="Extrato de Providências de Relatórios" size="5xl">
                <div className="space-y-6">
                    <p className="text-gray-700">Este extrato detalha o status de cada relatório. Use os filtros para buscar providências pendentes.</p>
                    
                    {/* FILTROS */}
                    <div className="bg-gray-50 p-4 rounded-lg shadow-sm border grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Pelotão</label>
                            <select 
                                value={extratoFiltros.pelotao}
                                onChange={(e) => setExtratoFiltros(prev => ({ ...prev, pelotao: e.target.value }))}
                                className="w-full border p-2 rounded text-sm bg-white"
                            >
                                <option value="All">Todos</option>
                                {allPelotoes.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cadete / Preceptor (Nome/QRA)</label>
                            <input 
                                type="text" 
                                placeholder="Buscar nome ou QRA..."
                                value={extratoFiltros.cadete}
                                onChange={(e) => setExtratoFiltros(prev => ({ ...prev, cadete: e.target.value }))}
                                className="w-full border p-2 rounded text-sm" 
                            />
                        </div>
                        <div className="md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data de Estágio</label>
                            <input 
                                type="date" 
                                value={extratoFiltros.data}
                                onChange={(e) => setExtratoFiltros(prev => ({ ...prev, data: e.target.value }))}
                                className="w-full border p-2 rounded text-sm" 
                            />
                        </div>
                        <div className="md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status Principal</label>
                            <select 
                                value={extratoFiltros.status}
                                onChange={(e) => setExtratoFiltros(prev => ({ ...prev, status: e.target.value }))}
                                className="w-full border p-2 rounded text-sm bg-white"
                            >
                                <option value="All">Todos os Status</option>
                                <option value="Avaliado">Avaliado (Finalizado)</option>
                                <option value="Aguardando Preceptor">Aguardando Preceptor</option>
                                <option value="Rascunho">Rascunho (Cadete)</option>
                                <option value="Pendente">Pendente (Iniciado)</option>
                            </select>
                        </div>
                        <div className="md:col-span-1 flex items-end gap-2">
                            <Button onClick={() => setExtratoFiltros({ cadete: '', data: '', status: 'All', pelotao: 'All' })} variant="secondary" className="flex-1">
                                Limpar
                            </Button>
                            <Button onClick={handlePrintExtrato} variant="outline" className="flex-1 border-blue-600 text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-1">
                                <PrinterIcon /> Imprimir
                            </Button>
                        </div>
                    </div>
                    
                    {/* TABELA DE RESULTADOS */}
                    <div className="overflow-x-auto border rounded-lg shadow-md">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Pelotão</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Cadete / Local</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Preceptor (QRA)</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Providência Necessária</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Ver</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {filteredExtratoLista.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-8 text-gray-500">Nenhum relatório encontrado com os filtros aplicados.</td></tr>
                                ) : (
                                    filteredExtratoLista.map((item, index) => {
                                        let statusColor = 'gray';
                                        if (item.status.includes('Avaliado')) statusColor = 'green';
                                        else if (item.status.includes('Aguardando')) statusColor = 'yellow';
                                        else if (item.status.includes('Rascunho')) statusColor = 'blue';
                                        else if (item.status.includes('Pendente')) statusColor = 'red';
                                        const colors = colorMap[statusColor];

                                        return (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 font-medium text-gray-800">{item.pelotao}</td>
                                                <td className="px-4 py-3 text-gray-700">
                                                    <span className="font-semibold">{item.cadete}</span>
                                                    <span className="block text-xs text-gray-500">{item.local} ({item.dataDisplay})</span>
                                                </td>
                                                <td className="px-4 py-3 text-gray-600">
                                                    {item.preceptor}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colors.bgLight} ${colors.text}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-red-600 font-medium text-sm">
                                                    {item.providencia}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <Button 
                                                        onClick={() => handleOpenReportView({ escolha_id: item.escolha_id, relatorioId: item.relatorioId })} 
                                                        variant="outline" 
                                                        className="text-xs py-1 px-2 flex items-center justify-center mx-auto"
                                                    >
                                                        <EyeIcon />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        {filteredExtratoLista.length > 0 && (
                            <div className="p-3 bg-gray-100 text-sm text-gray-600 font-medium">
                                Total de Relatórios no Extrato: {filteredExtratoLista.length}
                            </div>
                        )}
                    </div>
                    
                    <div className="flex justify-end pt-4 border-t">
                        <Button onClick={() => setShowExtratoModal(false)} variant="secondary">Fechar Extrato</Button>
                    </div>
                </div>
            </Modal>

            </div>
    );
};